import { createContext, useContext, ReactNode, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  initials: string;
  phone?: string;
  isAdmin: boolean;
  isActive: boolean;
  profileImageUrl?: string;
  lastLoginAt?: string;
  canViewFinancials?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  canViewFinancials: boolean;
  ssoConfigured: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  canViewFinancials: false,
  ssoConfigured: false,
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useLocation();

  const { data, isLoading } = useQuery({
    queryKey: ["auth-status"],
    queryFn: async () => {
      const res = await fetch("/api/auth/status");
      return res.json();
    },
    staleTime: 60000,
  });

  const logout = () => {
    window.location.href = "/auth/logout";
  };

  const value: AuthContextType = {
    user: data?.user || null,
    isLoading,
    isAuthenticated: data?.authenticated || false,
    isAdmin: data?.user?.isAdmin || false,
    canViewFinancials: data?.user?.canViewFinancials || data?.user?.isAdmin || false,
    ssoConfigured: data?.ssoConfigured || false,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, ssoConfigured } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && ssoConfigured && !isAuthenticated) {
      setLocation("/login");
    }
  }, [isLoading, ssoConfigured, isAuthenticated, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!ssoConfigured) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAdmin, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        setLocation("/login");
      } else if (!isAdmin) {
        setLocation("/");
      }
    }
  }, [isLoading, isAuthenticated, isAdmin, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return <>{children}</>;
}

export function RequireFinancials({ children }: { children: ReactNode }) {
  const { canViewFinancials, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        setLocation("/login");
      } else if (!canViewFinancials) {
        setLocation("/");
      }
    }
  }, [isLoading, isAuthenticated, canViewFinancials, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !canViewFinancials) {
    return null;
  }

  return <>{children}</>;
}
